import React, { useEffect, useRef, useState } from "react";

export default function App() {
  const originRef = useRef();
  const destinationRef = useRef();
  const mapRef = useRef();
  const [map, setMap] = useState(null);
  const [originLoc, setOriginLoc] = useState(null);
  const [destinationLoc, setDestinationLoc] = useState(null);
  const [preference, setPreference] = useState("cheapest");
  const [tripPlan, setTripPlan] = useState(null);
  const [loading, setLoading] = useState(false); // ✅ Added loading state

  const markers = useRef([]);
  const polylines = useRef([]);
  const bounds = useRef(null);

  useEffect(() => {
    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: { lat: 28.6, lng: 77.2 },
      zoom: 6,
      gestureHandling: "greedy",
      scrollwheel: true,
    });
    setMap(mapInstance);

    const oAuto = new window.google.maps.places.Autocomplete(originRef.current);
    oAuto.addListener("place_changed", () => {
      const place = oAuto.getPlace();
      if (place.geometry) {
        setOriginLoc(place.geometry.location);
      } else {
        alert("Invalid origin location");
      }
    });

    const dAuto = new window.google.maps.places.Autocomplete(destinationRef.current);
    dAuto.addListener("place_changed", () => {
      const place = dAuto.getPlace();
      if (place.geometry) {
        setDestinationLoc(place.geometry.location);
      } else {
        alert("Invalid destination location");
      }
    });
  }, []);

  const clearMap = () => {
    markers.current.forEach((m) => m.setMap(null));
    markers.current = [];
    polylines.current.forEach((p) => p.setMap(null));
    polylines.current = [];
    bounds.current = new window.google.maps.LatLngBounds();
  };

  const addMarker = (position, title, infoText = null) => {
    const marker = new window.google.maps.Marker({
      position,
      map,
      title,
    });
    const info = new window.google.maps.InfoWindow({
      content: `<strong>${title}</strong>${infoText ? `<br/><em>${infoText}</em>` : ""}`,
    });
    marker.addListener("click", () => info.open(map, marker));
    markers.current.push(marker);
    bounds.current.extend(position);
  };

  const addPolyline = (path, color = "#007bff") => {
    const polyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: color,
      strokeOpacity: 1.0,
      strokeWeight: 4,
      map,
    });
    polylines.current.push(polyline);
    path.forEach((point) => bounds.current.extend(point));
  };

  const findNearbyStation = (location, places, callback) => {
    const types = ["train_station", "subway_station", "bus_station", "transit_station", "metro_station"];
    let index = 0;

    const tryNext = () => {
      if (index >= types.length) {
        callback(null);
        return;
      }
      const type = types[index++];
      places.nearbySearch(
        { location, radius: 20000, type },
        (results, status) => {
          if (status === "OK" && results.length) {
            callback(results[0]);
          } else {
            tryNext();
          }
        }
      );
    };

    tryNext();
  };

  const planTrip = async () => {
    if (!originLoc || !destinationLoc) {
      alert("Please select valid origin and destination");
      return;
    }

    setLoading(true); // ✅ Start loading

    try {
      clearMap();

      const places = new window.google.maps.places.PlacesService(map);
      const matrix = new window.google.maps.DistanceMatrixService();
      const directions = new window.google.maps.DirectionsService();

      addMarker(originLoc, "Origin", "Start: Your Trip Begins Here");
      addMarker(destinationLoc, "Destination", "End: Final Stop");

      findNearbyStation(originLoc, places, (originStation) => {
        findNearbyStation(destinationLoc, places, (destinationStation) => {
          if (originStation) {
            addMarker(
              originStation.geometry.location,
              `Origin Station: ${originStation.name}`,
              "Mode Change: Taxi → Train/Metro"
            );
          }
          if (destinationStation) {
            addMarker(
              destinationStation.geometry.location,
              `Destination Station: ${destinationStation.name}`,
              "Mode Change: Train/Metro → Taxi/Bus"
            );
          }

          if (!originStation || !destinationStation) {
            console.warn("No stations found — fallback to direct taxi");
            matrix.getDistanceMatrix(
              {
                origins: [originLoc],
                destinations: [destinationLoc],
                travelMode: "DRIVING",
              },
              async (res, status) => {
                if (status !== "OK" || !res || !res.rows) {
                  alert("Error fetching direct taxi distance");
                  setLoading(false); // ✅ Stop loading on error
                  return;
                }

                const leg = res.rows[0].elements[0];
                addPolyline([originLoc, destinationLoc]);

                const directLeg = {
                  mode: "taxi",
                  distance_km: leg.distance.value / 1000,
                  duration_mins: leg.duration.value / 60,
                };

                const resp = await fetch(
                  "https://fareflowbackend-production.up.railway.app/api/planTrip",
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      origin: originRef.current.value,
                      destination: destinationRef.current.value,
                      preference,
                      legs: [directLeg],
                    }),
                  }
                );

                const data = await resp.json();
                setTripPlan(data);
                setLoading(false); // ✅ Stop loading
                map.fitBounds(bounds.current);
              }
            );
            return;
          }

          matrix.getDistanceMatrix(
            {
              origins: [originLoc],
              destinations: [originStation.geometry.location],
              travelMode: "DRIVING",
            },
            (res1, status1) => {
              if (status1 !== "OK" || !res1 || !res1.rows) {
                alert("Error fetching distance for origin taxi");
                setLoading(false); // ✅ Stop loading on error
                return;
              }

              const oLeg = res1.rows[0].elements[0];
              addPolyline([originLoc, originStation.geometry.location], "#28a745");

              matrix.getDistanceMatrix(
                {
                  origins: [destinationStation.geometry.location],
                  destinations: [destinationLoc],
                  travelMode: "DRIVING",
                },
                (res2, status2) => {
                  if (status2 !== "OK" || !res2 || !res2.rows) {
                    alert("Error fetching distance for destination taxi");
                    setLoading(false); // ✅ Stop loading on error
                    return;
                  }

                  const dLeg = res2.rows[0].elements[0];
                  addPolyline([destinationStation.geometry.location, destinationLoc], "#28a745");

                  directions.route(
                    {
                      origin: originStation.geometry.location,
                      destination: destinationStation.geometry.location,
                      travelMode: "TRANSIT",
                    },
                    async (dirRes, dirStatus) => {
                      if (dirStatus !== "OK") {
                        console.warn("Transit failed, fallback to driving...");
                        matrix.getDistanceMatrix(
                          {
                            origins: [originStation.geometry.location],
                            destinations: [destinationStation.geometry.location],
                            travelMode: "DRIVING",
                          },
                          async (res3, status3) => {
                            if (status3 !== "OK" || !res3 || !res3.rows) {
                              alert("Fallback driving failed too!");
                              setLoading(false); // ✅ Stop loading on error
                              return;
                            }

                            const fallbackLeg = res3.rows[0].elements[0];
                            addPolyline(
                              [originStation.geometry.location, destinationStation.geometry.location],
                              "#dc3545"
                            );

                            const legs = [
                              {
                                mode: "local_taxi",
                                distance_km: oLeg.distance.value / 1000,
                                duration_mins: oLeg.duration.value / 60,
                              },
                              {
                                mode: "train",
                                distance_km: fallbackLeg.distance.value / 1000,
                                duration_mins: fallbackLeg.duration.value / 60,
                              },
                              {
                                mode: "local_taxi",
                                distance_km: dLeg.distance.value / 1000,
                                duration_mins: dLeg.duration.value / 60,
                              },
                            ];

                            const resp = await fetch(
                              "https://fareflowbackend-production.up.railway.app/api/planTrip",
                              {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  origin: originRef.current.value,
                                  destination: destinationRef.current.value,
                                  preference,
                                  legs,
                                }),
                              }
                            );

                            const data = await resp.json();
                            setTripPlan(data);
                            setLoading(false); // ✅ Stop loading
                            map.fitBounds(bounds.current);
                          }
                        );
                        return;
                      }

                      const intercity = dirRes.routes[0].legs[0];
                      addPolyline(
                        [originStation.geometry.location, destinationStation.geometry.location],
                        "#dc3545"
                      );

                      const legs = [
                        {
                          mode: "local_taxi",
                          distance_km: oLeg.distance.value / 1000,
                          duration_mins: oLeg.duration.value / 60,
                        },
                        {
                          mode: "train",
                          distance_km: intercity.distance.value / 1000,
                          duration_mins: intercity.duration.value / 60,
                        },
                        {
                          mode: "local_taxi",
                          distance_km: dLeg.distance.value / 1000,
                          duration_mins: dLeg.duration.value / 60,
                        },
                      ];

                      const resp = await fetch(
                        "https://fareflowbackend-production.up.railway.app/api/planTrip",
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            origin: originRef.current.value,
                            destination: destinationRef.current.value,
                            preference,
                            legs,
                          }),
                        }
                      );

                      const data = await resp.json();
                      setTripPlan(data);
                      setLoading(false); // ✅ Stop loading
                      map.fitBounds(bounds.current);
                    }
                  );
                }
              );
            }
          );
        });
      });
    } catch (err) {
      console.error(err);
      setLoading(false); // ✅ Stop loading on error
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>🚦 Smart Multi-Mode Fare Planner</h1>

      <div style={styles.controls}>
        <input ref={originRef} placeholder="🌍 Origin" style={styles.input} />
        <input ref={destinationRef} placeholder="📍 Destination" style={styles.input} />
        <select
          value={preference}
          onChange={(e) => setPreference(e.target.value)}
          style={styles.select}
        >
          <option value="cheapest">Cheapest</option>
          <option value="fastest">Fastest</option>
        </select>
        <button onClick={planTrip} style={styles.button}>
          Plan Trip
        </button>
      </div>

      <div ref={mapRef} style={styles.map}></div>

      {loading && (
        <div style={styles.loading}>
          <p>⏳ Planning your trip...</p>
        </div>
      )}

      {tripPlan && (
        <div style={styles.results}>
          <h3 style={styles.resultsTitle}>📌 Trip Details</h3>
          {tripPlan.legs.map((leg, idx) => (
            <div key={idx} style={styles.card}>
              <h4 style={styles.cardTitle}>{`🚗 Leg ${idx + 1}: ${leg.mode.toUpperCase()}`}</h4>
              <p>
                <strong>Fare:</strong> {leg.fare_estimate}
              </p>
              <p style={styles.cardDetails}>{leg.details}</p>
            </div>
          ))}
          <div style={styles.summary}>
            <p>
              <strong>Total Fare:</strong> {tripPlan.total_estimate}
            </p>
            <p>
              <strong>Total Time:</strong> {tripPlan.total_time}
            </p>
            <p style={{ fontStyle: "italic", marginTop: "0.5rem" }}>
              {tripPlan.recommendation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    margin: "0 auto",
    padding: "2rem",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    color: "black",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #486770ff 0%, #062b3eff 100%)",
    display: "flex",
    flexDirection: "column",
  },
  heading: {
    textAlign: "center",
    fontSize: "2rem",
    marginBottom: "1.5rem",
    color: "#ffffffff",
  },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem",
    justifyContent: "center",
    marginBottom: "1.5rem",
  },
  input: {
    padding: "0.75rem 1rem",
    border: "1px solid #ccc",
    borderRadius: "8px",
    minWidth: "200px",
    fontSize: "1rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    outline: "none",
  },
  select: {
    padding: "0.75rem 1rem",
    border: "1px solid #ccc",
    borderRadius: "8px",
    fontSize: "1rem",
    background: "#fff",
  },
  button: {
    padding: "0.75rem 1.5rem",
    background: "#293e54ff",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "1rem",
    cursor: "pointer",
    transition: "background 0.3s",
  },
  map: {
    height: "400px",
    width: "100%",
    borderRadius: "12px",
    border: "2px solid #eee",
    marginBottom: "2rem",
  },
  loading: {
    textAlign: "center",
    color: "white",
    fontSize: "1.2rem",
    padding: "1rem",
    background: "#00000055",
    borderRadius: "8px",
    marginTop: "1rem",
    backdropFilter: "blur(2px)",
  },
  results: {
    marginTop: "1rem",
  },
  resultsTitle: {
    fontSize: "1.5rem",
    marginBottom: "1rem",
    textAlign: "center",
    color: "white",
  },
  card: {
    background: "#ffffffcc",
    border: "1px solid #ddd",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    padding: "1rem 1.5rem",
    marginBottom: "1rem",
    backdropFilter: "blur(3px)",
  },
  cardTitle: {
    margin: "0 0 0.5rem 0",
    fontSize: "1.1rem",
  },
  cardDetails: {
    color: "black",
  },
  summary: {
    textAlign: "center",
    marginTop: "1.5rem",
    padding: "1rem",
    background: "#ffffffcc",
    borderRadius: "8px",
    backdropFilter: "blur(2px)",
  },
};
